const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
export const AUTH_EXPIRED_EVENT = 'pps-auth-expired';
export const AUTH_EXPIRED_BROADCAST_KEY = 'pps-student-auth-expired';
export type AuthExpiredReason =
  | 'token_expired'
  | 'token_invalid'
  | 'session_missing'
  | 'session_not_found'
  | 'session_revoked'
  | 'device_replaced'
  | 'account_blocked'
  | 'logged_out'
  | 'account_unavailable'
  | 'unknown';

type AuthSessionPayload = {
  accessToken: string;
  refreshToken: string;
  expiresInSeconds: number;
  user: unknown;
};

export type AuthExpiredEventDetail = {
  message: string;
  reason: AuthExpiredReason;
  errorCode?: string;
};

function isAbsoluteHttpUrl(value: string) {
  return /^https?:\/\//i.test(value);
}

export function resolveApiUrl(path?: string | null) {
  if (!path) {
    return path ?? undefined;
  }
  if (isAbsoluteHttpUrl(path) || !isAbsoluteHttpUrl(BASE_URL)) {
    return path;
  }
  return new URL(path, BASE_URL).toString();
}

export function isActivePracticeRoute(pathname: string) {
  return pathname === '/practice' || pathname.startsWith('/practice/');
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private authExpiredNotified = false;
  private onSessionRefresh?: (payload: AuthSessionPayload) => void;
  private onSessionExpired?: (detail: AuthExpiredEventDetail) => void;

  getAccessToken() {
    return this.accessToken;
  }

  setToken(token: string | null) {
    this.setAuthSession(token, this.refreshToken);
  }

  setAuthSession(accessToken: string | null, refreshToken: string | null) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (accessToken || refreshToken) {
      this.authExpiredNotified = false;
    }
  }

  configureAuthLifecycle(handlers: {
    onSessionRefresh?: (payload: AuthSessionPayload) => void;
    onSessionExpired?: (detail: AuthExpiredEventDetail) => void;
  }) {
    this.onSessionRefresh = handlers.onSessionRefresh;
    this.onSessionExpired = handlers.onSessionExpired;
  }

  async request<T>(path: string, options: RequestInit = {}, retryOnAuthFailure = true): Promise<T> {
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    if (this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    const response = await fetch(`${BASE_URL}${path}`, { ...options, headers, credentials: 'include' });
    const text = await response.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }

    if (response.status === 401 && retryOnAuthFailure && this.shouldAttemptSilentRefresh(path)) {
      const refreshed = await this.tryRefreshSession();
      if (refreshed) {
        return this.request<T>(path, options, false);
      }
    }

    if ((response.status === 401 || response.status === 403) && this.shouldNotifyAuthExpired(path)) {
      this.notifyAuthExpired({
        message: json?.error?.message || '登录态已失效，请重新登录后继续练习。',
        reason: this.resolveAuthExpiredReason(json?.error?.code, json?.error?.details),
        errorCode: json?.error?.code,
      });
    }

    if (!response.ok || !json?.success) {
      throw new ApiRequestError(
        json?.error?.code || 'UNKNOWN',
        json?.error?.message || json?.message || 'Request failed',
        json,
        response.status,
      );
    }
    return json.data as T;
  }

  get<T>(path: string, init: RequestInit = {}) { return this.request<T>(path, init); }
  post<T>(path: string, body?: unknown, init: RequestInit = {}) {
    return this.request<T>(path, {
      ...init,
      method: 'POST',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }
  patch<T>(path: string, body?: unknown, init: RequestInit = {}) {
    return this.request<T>(path, { ...init, method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
  }
  put<T>(path: string, body?: unknown, init: RequestInit = {}) {
    return this.request<T>(path, { ...init, method: 'PUT', body: body ? JSON.stringify(body) : undefined });
  }
  delete<T>(path: string, init: RequestInit = {}) { return this.request<T>(path, { ...init, method: 'DELETE' }); }

  private shouldAttemptSilentRefresh(path: string) {
    return this.shouldNotifyAuthExpired(path) && Boolean(this.refreshToken);
  }

  private shouldNotifyAuthExpired(path: string) {
    return !path.startsWith('/auth/') && !path.startsWith('/captcha/');
  }

  private async tryRefreshSession() {
    if (!this.refreshToken) {
      return false;
    }
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = this.performRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh() {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        refreshToken: this.refreshToken,
      }),
    });
    const text = await response.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    if (!response.ok || !json?.success) {
      this.accessToken = null;
      this.refreshToken = null;
      return false;
    }
    const payload = json.data as AuthSessionPayload;
    this.setAuthSession(payload.accessToken, payload.refreshToken);
    this.onSessionRefresh?.(payload);
    return true;
  }

  private resolveAuthExpiredReason(errorCode?: string, details?: Record<string, unknown>): AuthExpiredReason {
    const detailReason = typeof details?.reason === 'string' ? details.reason : '';
    if (detailReason === 'token_expired' || detailReason === 'token_invalid' || detailReason === 'session_missing'
      || detailReason === 'session_not_found' || detailReason === 'session_revoked' || detailReason === 'device_replaced'
      || detailReason === 'account_blocked' || detailReason === 'logged_out') {
      return detailReason;
    }
    if (errorCode === 'ACCOUNT_BLOCKED') {
      return 'account_blocked';
    }
    if (errorCode === 'SESSION_REVOKED') {
      return 'session_revoked';
    }
    if (errorCode === 'UNAUTHORIZED') {
      return 'account_unavailable';
    }
    return 'unknown';
  }

  private notifyAuthExpired(detail: AuthExpiredEventDetail) {
    if (this.authExpiredNotified) {
      return;
    }
    this.authExpiredNotified = true;
    this.accessToken = null;
    this.refreshToken = null;
    this.onSessionExpired?.(detail);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          AUTH_EXPIRED_BROADCAST_KEY,
          JSON.stringify({
            ...detail,
            at: new Date().toISOString(),
          }),
        );
      } catch {
        // Ignore storage write failures and still notify the current tab.
      }
    }
    window.dispatchEvent(
      new CustomEvent(AUTH_EXPIRED_EVENT, {
        detail,
      }),
    );
  }
}

export class ApiRequestError extends Error {
  constructor(public code: string, message: string, public response: unknown, public status?: number) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const apiClient = new ApiClient();
